/**
 * Lock screen state: isLocked, lock(), unlock(), requestLock(), registerPrepareForLock().
 * Persists to localStorage and syncs across tabs via BroadcastChannel.
 * On lock, invalidates the Supabase session so the app cannot be used without
 * re-authenticating via PIN (validatePin issues a fresh session).
 *
 * requestLock() reads Redux at call time; if recording/upload/completion is active,
 * shows a confirm modal, then runs the registered prepare callback (e.g. stop recording,
 * wait for uploads) before locking.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "react-toastify";
import {
    getStoredLockState,
    setStoredLockState,
    clearStoredLockState,
    getLockBroadcastChannel,
    type LockSyncMessage,
} from "@/features/lock-screen/lockStorage";
import { signOutLocal } from "@common/lib/signOut";
import { isRecorderActive } from "@common/lib/recorderActivity";
import store from "@/app/store";

type PrepareForLockFn = () => Promise<void>;

type LockContextValue = {
    isLocked: boolean;
    lock: () => void;
    unlock: () => void;
    requestLock: () => Promise<void>;
    registerPrepareForLock: (fn: PrepareForLockFn) => void;
};

const LockContext = createContext<LockContextValue | undefined>(undefined);

function isGlobalLockActivityActive(): boolean {
    return isRecorderActive();
}

export function LockProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState(getStoredLockState);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const prepareRef = useRef<PrepareForLockFn>(() => Promise.resolve());
    const remoteLockSyncInFlightRef = useRef(false);
    const isLockedRef = useRef(getStoredLockState().isLocked);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isPreparing, setIsPreparing] = useState(false);

    useEffect(() => {
        isLockedRef.current = state.isLocked;
    }, [state.isLocked]);

    const registerPrepareForLock = useCallback((fn: PrepareForLockFn) => {
        prepareRef.current = fn;
    }, []);

    const finalizeLockSession = useCallback(() => {
        const now = Date.now();
        setState({ isLocked: true, lockedAt: now });
        setShowConfirmModal(false);
        void signOutLocal();
        try {
            setStoredLockState({ isLocked: true, lockedAt: now });
        } catch {
            // Storage failed, but session is already invalidated (safe)
        }
    }, []);

    /** Another tab set the device locked; run same prepare path as requestLock (this tab may still be recording/uploading). */
    const syncLockFromRemote = useCallback(() => {
        if (remoteLockSyncInFlightRef.current) return;
        remoteLockSyncInFlightRef.current = true;
        void (async () => {
            try {
                if (isGlobalLockActivityActive()) {
                    await prepareRef.current();
                }
            } catch (err) {
                toast.error(err instanceof Error ? err.message : "Could not complete preparation for lock.");
            } finally {
                try {
                    finalizeLockSession();
                } finally {
                    remoteLockSyncInFlightRef.current = false;
                }
            }
        })();
    }, [finalizeLockSession]);

    // Sync from other tabs: BroadcastChannel. Channel is created on mount and closed on unmount
    // so we don't leave a channel with no listener (e.g. when LockProvider unmounts).
    useEffect(() => {
        const ch = getLockBroadcastChannel();
        channelRef.current = ch;
        if (!ch) return;
        const onMessage = (e: MessageEvent<LockSyncMessage>) => {
            const msg = e.data;
            if (msg?.type === "lock") {
                syncLockFromRemote();
            } else if (msg?.type === "unlock") {
                setState({ isLocked: false, lockedAt: 0 });
                clearStoredLockState();
            }
            // user_switch: for PR 7 we could reload or switch context
        };
        ch.addEventListener("message", onMessage);
        return () => {
            ch.removeEventListener("message", onMessage);
            try {
                ch.close();
            } catch {
                // ignore if close fails (e.g. in some envs)
            }
            channelRef.current = null;
        };
    }, [syncLockFromRemote]);

    // Sync from other tabs: localStorage storage event (fallback)
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === "happydoc_lock_state") {
                if (e.newValue === null) {
                    // Item removed → unlock
                    setState({ isLocked: false, lockedAt: 0 });
                } else if (e.newValue) {
                    try {
                        const parsed = JSON.parse(e.newValue) as { isLocked?: boolean; lockedAt?: number };
                        if (parsed?.isLocked) {
                            syncLockFromRemote();
                        } else {
                            setState({ isLocked: false, lockedAt: 0 });
                        }
                    } catch {
                        // ignore
                    }
                }
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [syncLockFromRemote]);

    // On visibility change (tab visible again), re-read localStorage in case we missed events
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState !== "visible") return;
            const stored = getStoredLockState();
            // First time we learn we're locked (e.g. missed BC/storage while backgrounded): run prepare, then session end.
            if (stored.isLocked && !isLockedRef.current) {
                syncLockFromRemote();
                return;
            }
            setState(stored);
            if (stored.isLocked) {
                void signOutLocal();
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, [syncLockFromRemote]);

    const lock = useCallback(() => {
        finalizeLockSession();
        channelRef.current?.postMessage({ type: "lock" });
    }, [finalizeLockSession]);

    const unlock = useCallback(() => {
        setState({ isLocked: false, lockedAt: 0 });
        clearStoredLockState();
        channelRef.current?.postMessage({ type: "unlock" });
    }, []);

    // Read from store at call time (via isRecorderActive) so we never use stale state
    // (e.g. shortcut fired before provider re-rendered after recording/upload started).
    const requestLock = useCallback(async () => {
        if (isRecorderActive()) {
            setShowConfirmModal(true);
            return;
        }
        lock();
    }, [lock]);

    const handleConfirmLock = useCallback(async () => {
        setIsPreparing(true);
        try {
            await prepareRef.current();
            lock();
            setShowConfirmModal(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Could not complete preparation for lock.");
            setShowConfirmModal(false);
        } finally {
            setIsPreparing(false);
        }
    }, [lock]);

    const handleCancel = useCallback(() => {
        setShowConfirmModal(false);
    }, []);

    const value: LockContextValue = {
        isLocked: state.isLocked,
        lock,
        unlock,
        requestLock,
        registerPrepareForLock,
    };

    return (
        <LockContext.Provider value={value}>
            {children}
            {showConfirmModal && (
                <div
                    className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="lock-confirm-title"
                >
                    <div className="bg-white rounded-lg shadow-xl max-w-md mx-4 p-6">
                        <h2 id="lock-confirm-title" className="text-lg font-semibold text-gray-900 mb-2">
                            Lock screen?
                        </h2>
                        <p className="text-sm text-gray-600 mb-6">
                            You're currently recording or uploading. Locking will stop the recording and save your
                            progress. Lock anyway?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={isPreparing}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmLock}
                                disabled={isPreparing}
                                className="px-4 py-2 text-sm font-medium text-white bg-max-600 rounded-md hover:bg-max-700 disabled:opacity-50"
                            >
                                {isPreparing ? "Saving…" : "Lock anyway"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </LockContext.Provider>
    );
}

export function useLock(): LockContextValue {
    const ctx = useContext(LockContext);
    if (ctx === undefined) {
        throw new Error("useLock must be used within LockProvider");
    }
    return ctx;
}

/** Poll until isUploadingGlobal is false or timeout (ms). Rejects on timeout to avoid locking while uploads are still in progress. */
export function waitForUploadsToComplete(timeoutMs = 120000): Promise<void> {
    const start = Date.now();
    return new Promise((resolve, reject) => {
        const check = () => {
            const s = store.getState();
            if (!s.globalState.isUploadingGlobal) {
                resolve();
                return;
            }
            if (Date.now() - start >= timeoutMs) {
                reject(
                    new Error(
                        "Uploads did not complete in time. Please wait for uploads to finish, then try locking again.",
                    ),
                );
                return;
            }
            setTimeout(check, 500);
        };
        check();
    });
}
